import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BottomSheet } from "@/components/shared/PagePrimitives";
import TaskDetailModal from "@/components/TaskDetailModal";
import { DONE_ST, isoToCz, czToIso } from "./taskUtils";
import { closesWithoutApproval } from "@/lib/tasks";
import { celebrate } from "@/lib/celebrate";

const PRIO = ["Nízká", "Střední", "Vysoká"];

// Obsluha řádku úkolu (tahy, šuplíky, detail) na jednom místě — používá ji
// Seznam v Úkolech i Přehled (zadání 98/B). Chování se oproti Tasks.js nemění.
//  - reload: zavolá se po dokončení / úpravě / smazání / vrácení.
//  - patchLocal(id, patch): volitelná optimistická úprava seznamu (v Úkolech
//    přes setTasks, ať Dnes/Priorita neblikají — zadání 96); když chybí,
//    použije se reload.
//  - navIds: pořadí id pro navigaci ←/→ v detailu.
export function useTaskRowActions({ reload, patchLocal, navIds = [] }) {
  const { user } = useAuth();
  const manager = canLeadTasks(user);   // vedení i projektant (173)
  const [openId, setOpenId] = useState(null);      // odemčený tahatelný řádek (mobil)
  const [modalTask, setModalTask] = useState(null);
  const [modalAction, setModalAction] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", deadline: "", priority: "Střední" });
  const [editCz, setEditCz] = useState("");        // termín ve tvaru DD.MM.RRRR
  const [delTask, setDelTask] = useState(null);

  // Třídění (termín, priorita) smí autor i vedení; zadání (název…) jen autor+majitel;
  // mazat jen autor (majitel pojistka). Server hlídá vše — klient jen skrývá.
  const canEdit = (t) => t.created_by === user?.id || manager;             // Dnes/Priorita = třídění
  const canIdentity = (t) => t.created_by === user?.id || user?.role === "OWNER"; // Upravit = zadání
  const canDelete = (t) => t.created_by === user?.id || user?.role === "OWNER";
  const canComplete = (t) => (t.assignees || []).includes(user?.id) && !DONE_ST.includes(t.status);
  // Vrátit hotový úkol smí řešitel / kdo dokončil / vedení (server hlídá).
  const canReopen = (t) => manager || t.completion?.by === user?.id || (t.assignees || []).includes(user?.id);

  const openTask = (id) => { setOpenId(null); setModalAction(null); setModalTask(id); };
  // Tah doprava / tlačítko dokončit: hotový úkol jen otevře, aktivní pošle do dialogu Dokončení.
  // 363: kdo úkol zavírá sám (vedení, vlastní úkol), dokončí ho rovnou bez okna — fotky se přidávají v detailu.
  const completeRow = (t, el) => {
    if (t.status === "Hotovo") { toast.info("Úkol už čeká na schválení."); return; }
    if (t.status === "Schváleno") { openTask(t.id); return; }
    if (closesWithoutApproval(t, user)) {
      setOpenId(null);
      patchLocal?.(t.id, { status: "Schváleno", completion: { at: new Date().toISOString(), by: user?.id, by_name: user?.name, photos: t.photos || [] } });
      api.post(`/tasks/${t.id}/complete`, { note: "", photos: [] })
        .then(() => { celebrate(el); toast.success("Úkol hotový."); reload?.(); })
        .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); reload?.(); });
      return;
    }
    setOpenId(null); setModalAction("complete"); setModalTask(t.id);
  };
  const onToday = (t) => {
    const today = new Date().toISOString().slice(0, 10);
    patchLocal?.(t.id, { deadline: today });
    api.patch(`/tasks/${t.id}`, { deadline: today })
      .then(() => { toast.success("Termín nastaven na dnes."); if (!patchLocal) reload?.(); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); reload?.(); });
  };
  const onPriority = (t) => {
    const next = PRIO[Math.min(PRIO.indexOf(t.priority) + 1, PRIO.length - 1)];
    if (next === t.priority) { toast.info("Priorita je už nejvyšší."); return; }
    patchLocal?.(t.id, { priority: next });
    api.patch(`/tasks/${t.id}`, { priority: next })
      .then(() => { toast.success(`Priorita zvýšena na ${next}.`); if (!patchLocal) reload?.(); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); reload?.(); });
  };
  const openEdit = (t) => { setOpenId(null); const iso = t.deadline ? t.deadline.slice(0, 10) : ""; setEditForm({ title: t.title || "", deadline: iso, priority: t.priority || "Střední" }); setEditCz(isoToCz(iso)); setEditTask(t); };
  const saveEdit = async () => {
    if (!editForm.title.trim()) return toast.error("Vyplňte název úkolu.");
    try { await api.patch(`/tasks/${editTask.id}`, { title: editForm.title.trim(), deadline: editForm.deadline || null, priority: editForm.priority }); toast.success("Úkol upraven."); setEditTask(null); reload?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDelete = (t) => { setOpenId(null); setDelTask(t); };
  const doDelete = async () => {
    try { await api.delete(`/tasks/${delTask.id}`); toast.success("Úkol smazán."); setDelTask(null); reload?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reopenRow = async (t) => {
    try { await api.post(`/tasks/${t.id}/reopen`, {}); toast.success("Úkol vrácen mezi rozpracované."); reload?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const swipeProps = { canEdit, canIdentity, canDelete, onToday, onPriority, onEdit: openEdit, onDelete: openDelete, openId, setOpenId };

  const sheets = (
    <>
      <TaskDetailModal taskId={modalTask} taskIds={navIds} onNavigate={setModalTask} initialAction={modalAction}
        open={!!modalTask} onOpenChange={(o) => { if (!o) { setModalTask(null); setModalAction(null); } }} onChanged={reload} />

      {/* Šuplík úpravy (mobil) */}
      <BottomSheet open={!!editTask} onOpenChange={(o) => { if (!o) setEditTask(null); }} title="Upravit úkol" testid="edit-task-sheet">
        <div className="space-y-4">
          <div><Label>Název</Label><Input data-testid="edit-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="h-11 mt-1" /></div>
          <div>
            <Label>Termín</Label>
            <div className="flex gap-2 mt-1">
              <Input type="date" data-testid="edit-deadline" value={editForm.deadline}
                onChange={(e) => { setEditForm({ ...editForm, deadline: e.target.value }); setEditCz(isoToCz(e.target.value)); }}
                className="h-11 w-[150px] min-w-0" />
              <Input data-testid="edit-deadline-text" placeholder="DD.MM.RRRR" value={editCz}
                onChange={(e) => { const v = e.target.value; setEditCz(v); const iso = czToIso(v); if (iso !== null) setEditForm((f) => ({ ...f, deadline: iso })); }}
                className="h-11 flex-1" />
            </div>
          </div>
          <div>
            <Label>Priorita</Label>
            <div className="flex gap-2 mt-1">
              {PRIO.map((p) => (
                <button key={p} data-testid={`edit-prio-${p}`} onClick={() => setEditForm({ ...editForm, priority: p })}
                  className={`flex-1 h-11 rounded-[12px] text-sm font-bold border transition-colors ${editForm.priority === p ? "bg-brand text-white border-brand" : "bg-secondary border-border text-muted-foreground"}`}>{p}</button>
              ))}
            </div>
          </div>
          <Button onClick={saveEdit} data-testid="edit-save" className="w-full h-12 font-bold">Uložit změny</Button>
          {editTask && canDelete(editTask) && (
            <button onClick={() => { const t = editTask; setEditTask(null); openDelete(t); }} data-testid="edit-delete-link"
              className="w-full text-center text-sm font-semibold text-destructive hover:underline">Smazat úkol</button>
          )}
        </div>
      </BottomSheet>

      {/* Šuplík potvrzení smazání */}
      <BottomSheet open={!!delTask} onOpenChange={(o) => { if (!o) setDelTask(null); }} title="Smazat úkol?" testid="delete-task-sheet">
        <p className="text-sm text-muted-foreground">„{delTask?.title}" se smaže i s komentáři a fotkami. Vrátit to nejde.</p>
        {delTask?.completion && <p className="text-sm text-destructive font-semibold mt-2">Úkol už někdo dokončil — smažeš i jeho popis a fotky.</p>}
        <div className="flex flex-col gap-2 mt-4">
          <Button onClick={doDelete} data-testid="delete-confirm" className="w-full h-12 font-bold bg-destructive hover:bg-destructive/90 text-white">Smazat úkol</Button>
          <Button variant="outline" onClick={() => setDelTask(null)} data-testid="delete-cancel" className="w-full h-12 font-bold">Nechat úkol</Button>
        </div>
      </BottomSheet>
    </>
  );

  return {
    swipeProps, openId, setOpenId,
    openTask, completeRow, canComplete,
    reopenRow, canReopen, canEdit, canIdentity, canDelete,
    sheets,
  };
}
